// src/disc/DiscComparisonResults.js
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Grid,
  List,
  ListItem,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';
import DiscTeamDiamond from './DiscTeamDiamond';
import { ReactComponent as DiscBaseSvg } from './DISC.svg';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  marginBottom: theme.spacing(3),
}));

const RankBadge = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.palette.warning.main,
  color: theme.palette.warning.contrastText,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold',
  fontSize: '1.2rem',
}));

// Plan de acción del análisis de equipo: los nuevos vienen como { dias30, dias60, dias90 };
// los análisis anteriores lo tienen como texto.
const isStructuredActionPlan = (plan) => !!plan && typeof plan === 'object' && !Array.isArray(plan);

const getActionPlanItems = (plan, days) => {
  const items = plan[`dias${days}`] || plan[`days${days}`];
  return Array.isArray(items) ? items.filter((item) => typeof item === 'string' && item.trim()) : [];
};

const toSectionText = (value) => {
  if (Array.isArray(value)) return value.join('\n');
  return typeof value === 'string' ? value : '';
};

export default function DiscComparisonResults({
  comparisonId,
  analysisType,
  onBack,
}) {
  const { t } = useTranslation();
  const { authToken, showAlert } = useStore.getState();

  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  // Iniciar polling al montar
  useEffect(() => {
    fetchComparison();
    startPolling();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [comparisonId]);

  const startPolling = () => {
    intervalRef.current = setInterval(() => {
      fetchComparison();
    }, 3000); // Cada 3 segundos
  };

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const fetchComparison = async () => {
    try {
      const { data } = await api.get(
        `/disc-comparisons/${comparisonId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (data.response && data.response.status_response === 'OK') {
        const comparisonData = data.response.data;
        setComparison(comparisonData);

        // Detener polling si está completado o con error
        if (comparisonData.status === 'completado' || comparisonData.status === 'error') {
          stopPolling();
          setLoading(false);
        }
      } else {
        throw new Error(data.response?.message || 'Error al obtener comparación');
      }
    } catch (err) {
      console.error('Error al obtener comparación:', err);
      setError(err.message);
      setLoading(false);
      stopPolling();
    }
  };

  const handleRetry = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data } = await api.post(
        `/disc-comparisons/${comparisonId}/retry`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (data.response && data.response.status_response === 'OK') {
        showAlert(t('disc.discComparisons.retryStarted'), 'success');
        startPolling();
      }
    } catch (err) {
      console.error('Error al reintentar:', err);
      showAlert(t('disc.discComparisons.retryError'), 'error');
      setLoading(false);
    }
  };

  // Descarga un PDF guardado en Storage con un nombre de archivo propio
  const downloadPdf = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error al descargar PDF:', error);
      // Fallback: abrir en nueva pestaña
      window.open(url, '_blank');
    }
  };

  const handleDownloadPDF = async () => {
    if (!comparison?.reportUrl) return;

    // Generar nombre de archivo dinámico
    const analysisTypeName = analysisType === 'compareCandidates' ? 'ranking' :
      analysisType === 'candidateVsBoss' ? 'compatibilidad' : 'equipo';
    await downloadPdf(comparison.reportUrl, `disc-${analysisTypeName}-${comparison.id || 'reporte'}.pdf`);
  };

  // PDF de los mapas grupales (solo análisis de equipo; PLAN_MAPAS_GRUPALES_DISC.md)
  const handleDownloadMapsPDF = async () => {
    if (!comparison?.mapsReportUrl) return;

    await downloadPdf(comparison.mapsReportUrl, `disc-mapas-equipo-${comparison.id || 'reporte'}.pdf`);
  };

  // Renderizar estado de procesamiento
  const renderProcessingState = () => (
    <StyledPaper>
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h5" gutterBottom>
          {t('disc.discComparisons.processing')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('disc.discComparisons.processingDescription')}
        </Typography>
        <LinearProgress sx={{ maxWidth: 400, mx: 'auto' }} />

        {comparison?.logs && comparison.logs.length > 0 && (
          <Box sx={{ mt: 3, textAlign: 'left', maxWidth: 600, mx: 'auto' }}>
            <Typography variant="caption" color="text.secondary">
              {t('disc.discComparisons.lastUpdate')}:
            </Typography>
            <Typography variant="caption" display="block">
              {comparison.logs[comparison.logs.length - 1].message}
            </Typography>
          </Box>
        )}
      </Box>
    </StyledPaper>
  );

  // Renderizar estado de error
  const renderErrorState = () => (
    <StyledPaper>
      <Alert
        severity="error"
        icon={<ErrorIcon />}
        action={
          <Button color="inherit" size="small" onClick={handleRetry}>
            <RefreshIcon sx={{ mr: 0.5 }} />
            {t('retry')}
          </Button>
        }
      >
        <Typography variant="subtitle2">
          {t('disc.discComparisons.processingError')}
        </Typography>
        <Typography variant="caption">
          {comparison?.error || error || t('disc.discComparisons.unknownError')}
        </Typography>
      </Alert>
    </StyledPaper>
  );

  // Renderizar perfil DISC
  const renderDiscProfile = (profile, primaryStyle) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {t('disc.discComparisons.naturalProfile')}:
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
        <Typography variant="h6">D:{profile.natural.D}%</Typography>
        <Typography variant="h6">I:{profile.natural.I}%</Typography>
        <Typography variant="h6">S:{profile.natural.S}%</Typography>
        <Typography variant="h6">C:{profile.natural.C}%</Typography>
      </Box>
      <Chip
        label={`${primaryStyle} ${t('disc.dominant')}`}
        color="primary"
        size="small"
      />
    </Box>
  );

  // ============================================
  // Vista 1: COMPARAR CANDIDATOS (Ranking)
  // ============================================
  const renderCompareCandidates = () => {
    const gptAnalysis = comparison.result; // Array con ranking
    const candidatesData = comparison.tests; // Datos completos

    if (!Array.isArray(gptAnalysis) || gptAnalysis.length === 0) {
      return (
        <Alert severity="warning">
          {t('disc.discComparisons.noResultsAvailable')}
        </Alert>
      );
    }

    // Combinar datos de GPT con datos completos de candidatos
    const enrichedResults = gptAnalysis.map(gptResult => {
      // Intentar match por email primero (datos nuevos)
      let candidateData = candidatesData.find(
        c => gptResult.candidateEmail && c.applicantEmail === gptResult.candidateEmail
      );

      // Fallback: match por nombre normalizado (datos antiguos)
      if (!candidateData) {
        candidateData = candidatesData.find(c => {
          const testName = (c.applicantName || '').trim().replace(/\s+/g, ' ');
          const resultName = (gptResult.candidateName || '').trim().replace(/\s+/g, ' ');
          return testName === resultName;
        });
      }

      return {
        ...gptResult,
        email: candidateData?.applicantEmail || 'N/A',
        discProfile: candidateData?.finalScores?.profile || {},
        primaryStyle: candidateData?.finalScores?.primaryStyle || 'N/A',
      };
    });

    // Extraer información del cargo y competencias
    const positionName = comparison.position_name;
    const positionDescription = comparison.position_description;
    const competencies = comparison.competencies || [];

    return (
      <Box>
        <StyledPaper>
          <Typography variant="h5" gutterBottom>
            📊 {t('disc.discComparisons.candidatesRanking')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('disc.discComparisons.analysisBasedOnCompetencies')}
          </Typography>
        </StyledPaper>

        {(positionName || positionDescription || (competencies && competencies.length > 0)) && (
          <StyledPaper>
            <Typography variant="h6" gutterBottom color="primary">
              📋 {t('disc.discComparisons.positionInfo')}
            </Typography>

            {positionName && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('position')}:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {positionName}
                </Typography>
              </Box>
            )}

            {positionDescription && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('disc.discComparisons.positionDescription')}:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {positionDescription}
                </Typography>
              </Box>
            )}

            {competencies && competencies.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('disc.discComparisons.evaluatedCompetencies')}:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {competencies.map((comp, idx) => (
                    <Chip
                      key={idx}
                      label={comp.name}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </StyledPaper>
        )}

        {/* Cards de candidatos */}
        {enrichedResults.map((candidate, index) => (
          <StyledPaper key={index}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <RankBadge>
                #{candidate.rank}
              </RankBadge>

              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">
                    {candidate.candidateName}
                  </Typography>
                  <Chip
                    label={`${candidate.primaryStyle} ${t('disc.dominant')}`}
                    color="primary"
                    size="small"
                  />
                </Box>

                <Typography variant="caption" color="text.secondary">
                  {candidate.email}
                </Typography>
              </Box>
            </Box>

            {renderDiscProfile(candidate.discProfile, candidate.primaryStyle)}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                {t('disc.discComparisons.fitAnalysis')}:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {candidate.fitAnalysis}
              </Typography>
            </Box>

            <Box sx={{ backgroundColor: '#e3f2fd', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                {t('disc.discComparisons.teamContribution')}:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {candidate.teamContribution}
              </Typography>
            </Box>
          </StyledPaper>
        ))}
      </Box>
    );
  };

  // ============================================
  // Vista 2: CANDIDATO VS JEFE
  // ============================================
  const renderCandidateVsBoss = () => {
    const gptAnalysis = comparison.result; // Array de análisis
    const candidatesData = comparison.tests; // Datos completos
    const bossTestId = comparison.boss_test_id;

    // Encontrar datos del jefe
    const bossData = candidatesData.find(t => t.id === bossTestId);

    if (!bossData) {
      return (
        <Alert severity="error">
          {t('disc.discComparisons.bossDataNotFound')}
        </Alert>
      );
    }

    if (!Array.isArray(gptAnalysis) || gptAnalysis.length === 0) {
      return (
        <Alert severity="warning">
          {t('disc.discComparisons.noResultsAvailable')}
        </Alert>
      );
    }

    // Combinar datos
    const enrichedResults = gptAnalysis.map(gptResult => {
      // Intentar match por email primero (datos nuevos)
      let candidateData = candidatesData.find(
        c => gptResult.candidateEmail && c.applicantEmail === gptResult.candidateEmail && c.id !== bossTestId
      );

      // Fallback: match por nombre normalizado (datos antiguos)
      if (!candidateData) {
        candidateData = candidatesData.find(c => {
          const testName = (c.applicantName || '').trim().replace(/\s+/g, ' ');
          const resultName = (gptResult.candidateName || '').trim().replace(/\s+/g, ' ');
          return testName === resultName && c.id !== bossTestId;
        });
      }

      return {
        ...gptResult,
        email: candidateData?.applicantEmail || 'N/A',
        discProfile: candidateData?.finalScores?.profile || {},
        primaryStyle: candidateData?.finalScores?.primaryStyle || 'N/A',
      };
    });

    const positionName = comparison.position_name;
    const positionDescription = comparison.position_description;
    const competencies = comparison.competencies || [];

    return (
      <Box>
        {(positionName || positionDescription || (competencies && competencies.length > 0)) && (
          <StyledPaper>
            <Typography variant="h6" gutterBottom color="primary">
              📋 {t('disc.discComparisons.positionInfo')}
            </Typography>

            {positionName && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('position')}:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {positionName}
                </Typography>
              </Box>
            )}

            {positionDescription && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('disc.discComparisons.positionDescription')}:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {positionDescription}
                </Typography>
              </Box>
            )}

            {competencies && competencies.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('disc.discComparisons.evaluatedCompetencies')}:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {competencies.map((comp, idx) => (
                    <Chip
                      key={idx}
                      label={comp.name}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </StyledPaper>
        )}

        {/* Card del Jefe */}
        <StyledPaper sx={{ border: '2px solid', borderColor: 'warning.main' }}>
          <Typography variant="overline" color="warning.main" fontWeight="bold">
            {t('disc.discComparisons.bossLabel')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Box>
              <Typography variant="h5">
                {bossData.applicantName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {bossData.applicantEmail}
              </Typography>
            </Box>
            <Chip
              label={`${bossData.finalScores?.primaryStyle} ${t('disc.dominant')}`}
              color="warning"
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('disc.discComparisons.naturalProfile')}:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography variant="h6">D:{bossData.finalScores?.profile?.natural?.D}%</Typography>
              <Typography variant="h6">I:{bossData.finalScores?.profile?.natural?.I}%</Typography>
              <Typography variant="h6">S:{bossData.finalScores?.profile?.natural?.S}%</Typography>
              <Typography variant="h6">C:{bossData.finalScores?.profile?.natural?.C}%</Typography>
            </Box>
          </Box>
        </StyledPaper>

        {/* Cards de Candidatos */}
        {enrichedResults.map((candidate, index) => (
          <StyledPaper key={index}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  {candidate.candidateName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {candidate.email}
                </Typography>
              </Box>
              <Chip
                label={`${candidate.primaryStyle} ${t('disc.dominant')}`}
                color="primary"
              />
            </Box>

            {renderDiscProfile(candidate.discProfile, candidate.primaryStyle)}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                {t('disc.discComparisons.complementarityAnalysis')}:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {candidate.complementarityAnalysis}
              </Typography>
            </Box>

            <Box sx={{ backgroundColor: '#fff3e0', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                {t('disc.discComparisons.workDynamics')}:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {candidate.workDynamics}
              </Typography>
            </Box>

            <Box sx={{ backgroundColor: '#e8f5e9', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                {t('disc.discComparisons.synergiesAndTensions')}:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {candidate.synergiesAndTensions}
              </Typography>
            </Box>
          </StyledPaper>
        ))}
      </Box>
    );
  };

  // ============================================
  // Vista 3: ANÁLISIS DE EQUIPO
  // ============================================
  const renderTeamAnalysis = () => {
    const gptAnalysis = comparison.result; // Objeto con las 7 secciones
    const teamContext = comparison.team_context || {};
    const teamName = teamContext.name || '';
    const teamDescription = teamContext.description || '';
    const teamObjectives = teamContext.objectives || '';

    if (!gptAnalysis || typeof gptAnalysis !== 'object') {
      return (
        <Alert severity="warning">
          {t('disc.discComparisons.noResultsAvailable')}
        </Alert>
      );
    }

    const sections = [
      { key: 'composition', title: t('disc.discComparisons.composition'), color: '#e3f2fd' },
      { key: 'collectiveStrengths', title: t('disc.discComparisons.collectiveStrengths'), color: '#e8f5e9' },
      { key: 'gaps', title: t('disc.discComparisons.gaps'), color: '#fff3e0' },
      { key: 'currentDynamics', title: t('disc.discComparisons.currentDynamics'), color: '#f3e5f5' },
      { key: 'roleRecommendations', title: t('disc.discComparisons.roleRecommendations'), color: '#e0f7fa' },
      { key: 'managementStrategies', title: t('disc.discComparisons.managementStrategies'), color: '#fce4ec' },
      { key: 'actionPlan', title: t('disc.discComparisons.actionPlan'), color: '#fff9c4' },
    ];

    // Preparar datos de miembros
    const teamMembers = comparison.tests.map(test => {
      const profile = test?.finalScores?.profile || {};
      const natural = profile?.natural || { D: 0, I: 0, S: 0, C: 0 };
      const adapted = profile?.adapted || { D: 0, I: 0, S: 0, C: 0 }; // ✅

      return {
        name: test.applicantName,
        natural: natural,
        adapted: adapted
      };
    });

    return (
      <Box>
        <StyledPaper>
          <Typography variant="h5" gutterBottom>
            👥 {t('disc.discComparisons.teamAnalysisTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {teamName}
          </Typography>
        </StyledPaper>

        {/* NUEVA SECCIÓN: Contexto del Equipo */}
        {(teamDescription || teamObjectives) && (
          <StyledPaper>
            <Typography variant="h6" gutterBottom>
              {t('disc.discComparisons.teamContext')}
            </Typography>

            {teamDescription && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  {t('disc.discComparisons.teamDescription')}:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {teamDescription}
                </Typography>
              </Box>
            )}

            {teamObjectives && (
              <Box>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  {t('disc.discComparisons.teamObjectives')}:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {teamObjectives}
                </Typography>
              </Box>
            )}
          </StyledPaper>
        )}

        <StyledPaper sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1976d2', mb: 3 }}>
            {t('disc.discComparisons.teamDiscMap')}
          </Typography>

          <DiscTeamDiamond
            BaseSvg={DiscBaseSvg}
            teamMembers={teamMembers}
          />
        </StyledPaper>

        {sections.map((section) => {
          const content = gptAnalysis[section.key];
          if (!content) return null;
          // Plan de acción nuevo { dias30, dias60, dias90 }: 3 columnas con check, como el Assessment de Ventas
          const isPlan306090 = section.key === 'actionPlan' && isStructuredActionPlan(content);
          const compositionSummary = section.key === 'composition' && typeof gptAnalysis.compositionSummary === 'string'
            ? gptAnalysis.compositionSummary.trim()
            : '';

          return (
            <StyledPaper key={section.key}>
              <Box sx={{ backgroundColor: section.color, p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {isPlan306090 ? t('disc.discComparisons.actionPlan306090') : section.title}
                </Typography>
              </Box>
              {isPlan306090 ? (
                <Grid container spacing={3}>
                  {[30, 60, 90].map((days) => (
                    <Grid item xs={12} md={4} key={days}>
                      <Typography variant="h6" color="primary" gutterBottom>
                        {t(`disc.discComparisons.days${days}`)}
                      </Typography>
                      <List dense>
                        {getActionPlanItems(content, days).map((accion, index) => (
                          <ListItem key={index} sx={{ px: 0, alignItems: 'flex-start' }}>
                            <CheckIcon sx={{ mr: 1, mt: 0.5, fontSize: 18, color: 'primary.main' }} />
                            <Typography variant="body2">{accion}</Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {toSectionText(content)}
                </Typography>
              )}
              {/* "En síntesis" de la composición (los análisis anteriores no lo tienen) */}
              {compositionSummary && (
                <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mt: 2 }}>
                  <Typography variant="body2">
                    <Box component="span" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {t('disc.discComparisons.inSummary')}:
                    </Box>{' '}
                    {compositionSummary}
                  </Typography>
                </Box>
              )}
            </StyledPaper>
          );
        })}
      </Box>
    );
  };

  // Renderizar resultados según el tipo
  const renderResults = () => {
    if (!comparison) return null;

    switch (analysisType) {
      case 'compareCandidates':
        return renderCompareCandidates();
      case 'candidateVsBoss':
        return renderCandidateVsBoss();
      case 'teamAnalysis':
        return renderTeamAnalysis();
      default:
        return (
          <Alert severity="info">
            {t('disc.discComparisons.unknownAnalysisType')}
          </Alert>
        );
    }
  };

  // Render principal
  if (loading && !comparison) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress size={60} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t('loading')}...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            variant="outlined"
          >
            {t('goBack')}
          </Button>

          {comparison?.status === 'error' && (
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
              variant="contained"
              color="warning"
            >
              {t('retry')}
            </Button>
          )}
        </Box>

        {comparison?.status === 'completado' && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* PDF de mapas grupales, aparte del informe (solo análisis de equipo; PLAN_MAPAS_GRUPALES_DISC.md) */}
            {analysisType === 'teamAnalysis' && (
              <Tooltip title={comparison?.mapsReportUrl ? '' : t('disc.discComparisons.groupMapsPdfPending')}>
                <span>
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadMapsPDF}
                    variant="outlined"
                    color="primary"
                    disabled={!comparison?.mapsReportUrl}
                  >
                    {t('disc.discComparisons.downloadGroupMapsPDF')}
                  </Button>
                </span>
              </Tooltip>
            )}

            <Button
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPDF}
              variant="contained"
              color="primary"
              disabled={!comparison?.reportUrl}
            >
              {t('disc.discComparisons.downloadPDF')}
            </Button>
          </Box>
        )}
      </Box>

      {comparison?.status === 'procesando' && renderProcessingState()}
      {comparison?.status === 'error' && renderErrorState()}
      {comparison?.status === 'completado' && renderResults()}
    </Box>
  );
}